import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface EventType {
  id: string;
  key: string;
  name_fr: string;
  name_en: string;
  name_es: string;
  icon?: string;
  color: string;
  is_active: boolean;
}

export interface ServerEvent {
  id: string;
  server_id: string;
  event_type_key: string;
  title_fr?: string;
  title_en?: string;
  title_es?: string;
  description_fr?: string;
  description_en?: string;
  description_es?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  event_type?: EventType;
}

export const useEventTypes = () => {
  return useQuery({
    queryKey: ["event-types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_types")
        .select("*")
        .eq("is_active", true)
        .order("name_fr");

      if (error) throw error;
      return data as EventType[];
    },
  });
};

export const useServerEvents = (serverId?: string) => {
  return useQuery({
    queryKey: ["server-events", serverId],
    queryFn: async () => {
      if (!serverId) return [];
      
      const { data, error } = await supabase
        .from("server_events")
        .select("*")
        .eq("server_id", serverId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ServerEvent[];
    },
    enabled: !!serverId,
  });
};

export const useCreateServerEvent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (eventData: Omit<ServerEvent, "id" | "created_at" | "event_type">) => {
      const { data, error } = await supabase
        .from("server_events")
        .insert(eventData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Utiliser setQueryData pour une mise à jour optimiste au lieu d'invalider
      queryClient.setQueryData(
        ["server-events", data.server_id],
        (oldData: any) => {
          if (!oldData) return [data];
          return [...oldData, data];
        }
      );
      toast({
        title: "Événement créé",
        description: "L'événement a été ajouté avec succès",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: "Impossible de créer l'événement",
        variant: "destructive",
      });
      console.error("Error creating server event:", error);
    },
  });
};

export const useUpdateServerEvent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...eventData }: Partial<ServerEvent> & { id: string }) => {
      const { data, error } = await supabase
        .from("server_events")
        .update(eventData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalidation plus spécifique pour éviter de fermer la dialog
      queryClient.invalidateQueries({ 
        queryKey: ["server-events", data.server_id],
        exact: true 
      });
      toast({
        title: "Événement modifié",
        description: "L'événement a été mis à jour avec succès",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'événement",
        variant: "destructive",
      });
      console.error("Error updating server event:", error);
    },
  });
};

export const useDeleteServerEvent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (eventId: string) => {
      // Récupérer le server_id avant la suppression pour l'invalidation spécifique
      const { data: eventData } = await supabase
        .from("server_events")
        .select("server_id")
        .eq("id", eventId)
        .single();

      const { error } = await supabase
        .from("server_events")
        .update({ is_active: false })
        .eq("id", eventId);

      if (error) throw error;
      return { eventId, serverId: eventData?.server_id };
    },
    onSuccess: (data) => {
      // Utiliser setQueryData pour une mise à jour optimiste au lieu d'invalider
      if (data.serverId) {
        queryClient.setQueryData(
          ["server-events", data.serverId],
          (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.filter((event: any) => event.id !== data.eventId);
          }
        );
      }
      toast({
        title: "Événement supprimé",
        description: "L'événement a été désactivé avec succès",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'événement",
        variant: "destructive",
      });
      console.error("Error deleting server event:", error);
    },
  });
};